import BrandLogo from './BrandLogo';
import { useConfig } from '../context/ConfigContext';
import { useClientSubscription } from '../hooks/useClientSubscription';
import { formatMoney } from '../utils/currencyUtil';
import { formatPlanDateInZone } from '../utils/subscriptionDateUtil';
import { BRAND_LOGO_URL } from '../constants/bookingConst';
import {
  POS_APPLICATION_STATUS,
  POS_SUBSCRIPTION_REVIEW_WINDOW,
  POS_SUBSCRIPTION_VENDOR_NAME,
} from '../constants/posConst';
import '../css/PosSubscription.css';

/**
 * The "your free trial / subscription has expired" card: the club's name, what
 * ended and when, a Renew button (or the renewal already under review), and
 * who to contact.
 *
 * Shared by the two ways a till can be shut — an administrator disabling the
 * client (PosClientDisabledScreen) and the plan simply running out
 * (PosExpiredLock) — so the club reads the same message either way.
 *
 * @param {object} props
 * @param {() => void} props.onRenew - opens the Subscribe dialog
 * @returns {JSX.Element}
 */
function PosExpiredCard({ onRenew }) {
  const { config, client, clientId } = useConfig();
  const { subscription, applications } = useClientSubscription(clientId);

  const clubName = config?.courtName || client?.name || 'this club';
  const isTrial = Boolean(subscription?.isTrial);
  const endedOn = formatPlanDateInZone(subscription?.endDate, client?.timezone);
  const pending = applications.find((application) => application.status === POS_APPLICATION_STATUS.PENDING);
  const latest = applications[0];
  const rejected = !pending && latest?.status === POS_APPLICATION_STATUS.REJECTED ? latest : null;

  return (
    <div className="ps-lock-card" role="alert">
      <div className="ps-lock-club">
        <BrandLogo src={config?.logoUrl || BRAND_LOGO_URL} alt={clubName} className="ps-lock-logo" />
        <span className="ps-lock-club-name">{clubName}</span>
      </div>

      <span className="ps-lock-tag">Access paused</span>

      <h1 className="ps-lock-title">
        {isTrial ? 'Your free trial has expired' : 'Your subscription has expired'}
      </h1>

      <p className="ps-lock-lead">
        {subscription?.planName && endedOn
          ? <>Your <strong>{subscription.planName}</strong> {isTrial ? '' : 'plan '}ended on <strong>{endedOn}</strong>. </>
          : null}
        Please renew your subscription to continue using this till.
      </p>

      <p className="ps-lock-reassure">
        Your products, sales records and settings are safe — everything will be exactly as you
        left it once your subscription is active again.
      </p>

      {pending ? (
        <div className="ps-lock-status">
          <strong>Your renewal is under review</strong>
          <span>
            {pending.planName} · {formatMoney(pending.total, config)}. {POS_SUBSCRIPTION_VENDOR_NAME} usually
            reviews applications within {POS_SUBSCRIPTION_REVIEW_WINDOW}. This page will open by itself as
            soon as it is approved.
          </span>
        </div>
      ) : (
        <>
          {rejected && (
            <div className="ps-lock-status ps-lock-status-bad">
              <strong>Your last application was not approved</strong>
              <span>{rejected.rejectReason || 'No reason was given.'}</span>
            </div>
          )}
          <button type="button" className="ps-lock-cta" onClick={onRenew}>
            {isTrial ? 'Subscribe now' : 'Renew subscription'}
          </button>
        </>
      )}

      <div className="ps-lock-divider" />

      <p className="ps-lock-help">
        If you think this is an issue, please contact your system administrator.
      </p>
    </div>
  );
}

export default PosExpiredCard;
